import { FC, useEffect, useMemo, useState } from 'react';

import { useTour } from '@reactour/tour';
import { useFormikContext } from 'formik';
import { compact, debounce, startCase, upperCase } from 'lodash';
import { useGetAccountUsers, useGetLeads } from 'modules/account/contact/hooks';
import { Button } from 'ui/components/common';
import {
  Autocomplete,
  Input,
  Radio,
  RadioGroup,
  SelectAvatar,
  TagsField,
} from 'ui/components/form';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import useModal from 'ui/partials/Modal/useModal';
import { SIZE, TASK_PRIORITY } from 'utils/constants';
import { cn } from 'utils/helpers';
import { TOUR_SELECTOR } from 'ui/partials/Tour';

import { SIMPLE_TASK_FORM } from './schema';

const types = [
  { label: 'TO DO', value: 'TODO' },
  { label: 'EMAIL', value: 'EMAIL' },
  { label: 'CALL', value: 'CALL' },
];

type SimpleTaskFormProps = {
  onCancel?: () => void;
  isLoading?: boolean;
};

const SimpleTaskForm: FC<SimpleTaskFormProps> = ({
  isLoading = false,
  onCancel,
}) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setValues,
    setFieldError,
    setErrors,
  } = useFormikContext<SIMPLE_TASK_FORM>();
  const { hideModal } = useModal();
  const { isOpen } = useTour();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      hideModal();
    }
  };

  const [accountUserKeyword, setAccountUserKeyword] = useState('');
  const [leadKeyword, setLeadKeyword] = useState('');
  const { data: leads } = useGetLeads({ keyword: leadKeyword });
  const { data: accountUsers } = useGetAccountUsers({
    keyword: accountUserKeyword,
  });

  useEffect(() => {
    if (isOpen) {
      setValues({
        priority: 'HIGH',
        dueAt: new Date(),
        leadId: 'text-user',
        title: 'How to create a Task',
        type: 'TODO',
        accountUserId: ['test-admin'],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const debouncedSearch = debounce((string, setter) => setter(string), 800);

  const accountUserItems = useMemo<AvatarOption[]>(() => {
    if (isOpen) {
      return [
        {
          photoUrl: '',
          itemLabel: 'Jane Doe',
          description: 'sample@admin.com',
          value: 'test-admin',
        },
      ];
    }

    return (
      accountUsers?.data?.map((accountUser) => ({
        photoUrl: accountUser.photoUrl,
        itemLabel: accountUser.fullName,
        description: accountUser.emailAddress,
        value: accountUser._id,
      })) ?? []
    );
  }, [accountUsers?.data, isOpen]);

  const filterTaskType = (inputValue: string) => {
    return types.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };
  const loadTaskTypeOptions = (
    inputValue: string,
    callback: (options: typeof types) => void,
  ) => {
    setTimeout(() => {
      callback(filterTaskType(inputValue));
    }, 1000);
  };

  const contactItems = useMemo<AvatarOption[]>(() => {
    if (isOpen) {
      return [
        {
          photoUrl: '',
          itemLabel: 'John Doe',
          description: 'sample@user.com',
          value: 'test-user',
        },
      ];
    }

    return (
      leads?.data?.map((lead) => ({
        photoUrl: lead.photoUrl,
        itemLabel: lead.fullName,
        description: lead.emailAddress,
        value: lead._id,
      })) ?? []
    );
  }, [isOpen, leads?.data]);

  const loadContactItems = (
    search = '',
    cb: (options: AvatarOption[]) => void,
  ) => {
    debouncedSearch(search, setLeadKeyword);

    cb(contactItems);
  };

  const defaultContacts = useMemo(() => {
    if (isOpen) {
      return [
        {
          photoUrl: '',
          itemLabel: 'John Doe',
          description: 'sample@user.com',
          value: 'test-user',
        },
      ];
    }

    return contactItems?.filter((i) => values.leadId?.includes(i.value));
  }, [contactItems, isOpen, values.leadId]);

  const loadAccountUserItems = (
    search = '',
    cb: (options: AvatarOption[]) => void,
  ) => {
    debouncedSearch(search, setAccountUserKeyword);

    cb(accountUserItems);
  };

  const defaultAccountUsers = useMemo(() => {
    if (isOpen) {
      return [
        {
          photoUrl: '',
          itemLabel: 'Jane Doe',
          description: 'sample@admin.com',
          value: 'test-admin',
        },
      ];
    }

    return accountUserItems?.filter((i) =>
      values.accountUserId?.includes(i.value),
    );
  }, [accountUserItems, isOpen, values.accountUserId]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        className="space-y-7"
        data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.FORM.GENERAL.FORM}
      >
        <Input
          className="w-full"
          type="text"
          name="title"
          size={SIZE.Medium}
          textType={errors.title && touched.title ? 'danger' : 'default'}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.title}
          placeholder="Input title"
          subText={errors.title && touched.title ? errors.title : ''}
          label="Title"
          disabled={isSubmitting}
          required
        />
        <Autocomplete
          className="w-full"
          name="type"
          size={SIZE.Medium}
          state={errors.type && touched.type ? 'danger' : undefined}
          onChange={(val: any) => setFieldValue('type', val.value)}
          defaultValue={[
            {
              label: startCase(upperCase(values.type)),
              value: values.type,
            },
          ]}
          placeholder="Select"
          subText={errors.type && touched.type ? errors.type : ''}
          label="Task Type"
          required
          loadOptions={loadTaskTypeOptions}
          isDisabled={isSubmitting}
          isClearable={false}
        />

        <div>
          <h4 className="mb-2 text-sm">Priority</h4>
          <RadioGroup groupClassName="flex gap-3 flex-wrap required">
            {TASK_PRIORITY.map((p) => (
              <Radio
                name="priority"
                label={p.label}
                value={p.value}
                key={p.value}
                checked={p.value === values.priority}
                defaultChecked={p.value === values.priority}
                onClick={() => setFieldValue('priority', p.value)}
              />
            ))}
          </RadioGroup>
        </div>

        <div className="flex-1">
          <SelectAvatar
            {...(errors.leadId && { state: 'danger' })}
            className="w-full"
            loadOptions={loadContactItems}
            subText={
              errors.leadId && touched.leadId ? errors.leadId : undefined
            }
            label="Associated Contact"
            onChange={(val) => {
              setFieldValue('leadId', (val as AvatarOption).value);
            }}
            onClear={() => setFieldValue('leadId', null)}
            size={SIZE.Small}
            name="leadId"
            placeholder="Select"
            defaultOptions
            required
            defaultValue={defaultContacts}
          />
        </div>
        <SelectAvatar
          {...(errors.accountUserId && { state: 'danger' })}
          className="w-full"
          loadOptions={loadAccountUserItems}
          subText={
            errors.accountUserId && touched.accountUserId
              ? (errors.accountUserId as string)
              : undefined
          }
          label="Assign to"
          onChange={(val) => {
            setFieldValue(
              'accountUserId',
              (val as AvatarOption[]).map((v) => v.value),
            );
          }}
          onClear={() => setFieldValue('accountUserId', [])}
          size={SIZE.Small}
          name="accountUserId"
          isMulti
          placeholder="Select"
          defaultOptions
          required
          isDisabled={isSubmitting}
          defaultValue={defaultAccountUsers}
        />
        <TagsField
          className="col-span-4 w-full"
          onChange={(val) =>
            setFieldValue(
              'tagId',
              val?.map((tag) => tag.value),
            )
          }
          defaultValue={compact(values.tagId)}
          setError={(err) => {
            if (err) {
              setFieldError(
                'tagId',
                'Tags cannot have spaces and special characters.',
              );
            } else {
              setErrors({});
            }
          }}
        />
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'px-2.5 pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={handleCancel}
          label="CANCEL"
          size={SIZE.Small}
          intent="tertiary"
          disabled={isSubmitting}
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.FORM.GENERAL.SAVE_BUTTON}
          type="submit"
          size={SIZE.Small}
          label="CREATE TASK"
          disabled={isSubmitting || isLoading}
        />
      </div>
    </form>
  );
};

export default SimpleTaskForm;
